/**
 * Totais guardados prontos na linha, refeitos depois de aplicar o que veio de outro aparelho
 * (ADR 0163). O saldo da conta (`accounts.current_balance`) e o valor da caixinha
 * (`reservas.current_amount`) são somas do histórico; o sync junta por célula com
 * last-write-wins, então quando os dois aparelhos lançaram antes de se verem os lançamentos
 * chegam todos, mas o total fica com o número de um deles só.
 *
 * Refaz só o que o lote tocou: a conta de cada lançamento que chegou e a caixinha de cada
 * movimento. O aparelho de origem sempre regrava o total no mesmo passo em que escreve o
 * lançamento, então uma conta ou caixinha cujo movimento foi apagado de vez (sem linha para
 * consultar) chega pela própria linha.
 *
 * Roda com a captura LIGADA (como o `dedupeOccurrences`): a correção viaja para os outros
 * aparelhos, inclusive os que ainda estão num build sem este conserto.
 */

import { inArray } from 'drizzle-orm';
import { db } from '@/db/client';
import { reservaMovements, transactions } from '@/db/schema';
import { reconcileAccountBalances } from '@/db/repositories/accountRepository';
import { reconcileReservaAmounts } from '@/db/repositories/reservaRepository';
import { type Message } from './messages';

/** Teto de parâmetros por `IN (...)`: o join traz a base inteira num lote só. */
const LOOKUP_CHUNK = 500;

function rowsOf(incoming: readonly Message[], table: string): string[] {
    return [...new Set(incoming.filter((m) => m.table === table).map((m) => m.row))];
}

function chunked<T>(items: readonly T[]): T[][] {
    const out: T[][] = [];
    for (let i = 0; i < items.length; i += LOOKUP_CHUNK) out.push(items.slice(i, i + LOOKUP_CHUNK));
    return out;
}

/** Devolve quantos totais corrigiu. Chamar dentro de `runInTransaction`. */
export async function reconcileDerivedTotals(incoming: readonly Message[]): Promise<number> {
    const accountIds = new Set(rowsOf(incoming, 'accounts'));
    for (const ids of chunked(rowsOf(incoming, 'transactions'))) {
        const rows = await db
            .select({ accountId: transactions.accountId, destinationId: transactions.transferDestinationAccountId })
            .from(transactions)
            .where(inArray(transactions.id, ids));
        for (const row of rows) {
            if (row.accountId) accountIds.add(row.accountId);
            if (row.destinationId) accountIds.add(row.destinationId);
        }
    }

    const reservaIds = new Set(rowsOf(incoming, 'reservas'));
    for (const ids of chunked(rowsOf(incoming, 'reserva_movements'))) {
        const rows = await db
            .select({ reservaId: reservaMovements.reservaId })
            .from(reservaMovements)
            .where(inArray(reservaMovements.id, ids));
        for (const row of rows) reservaIds.add(row.reservaId);
    }

    return (await reconcileAccountBalances(accountIds)) + (await reconcileReservaAmounts(reservaIds));
}
